import type { OnChanges, OnDestroy } from '@angular/core';
import { Directive, Inject, Input } from '@angular/core';
import { LeafletDirective } from '@bluehalo/ngx-leaflet';
import type { Map } from 'leaflet';
import { LatLngBounds } from 'leaflet';
import type { Subscription } from 'rxjs';
import { MapOrganizationAreaService } from '../map-organization-area.service';

/**
 * Focuses a leaflet map when it opens, and whenever the bound value changes.
 *
 * - `[mtxAutoFocus]="bounds"` with a valid value fits those bounds (the component's own content).
 * - A null/empty value — or the bare `mtxAutoFocus` attribute — falls back to the organization's
 *   area (via {@link MapOrganizationAreaService}), and only rests on the zoomed-out default when the
 *   org has no located vehicles.
 *
 * Re-focuses when the bound value changes (e.g. async content or a filter re-run), but never
 * overrides the map after the user has panned it.
 */
@Directive({
  selector: '[mtxAutoFocus]',
  standalone: false,
})
export class AutoFocusDirective implements OnChanges, OnDestroy {

  /** Bounds to focus on, or null/empty (the bare attribute) to fall back to the organization area. */
  @Input('mtxAutoFocus') public focusBounds?: LatLngBounds | null | '';

  private map?: Map;

  private userPanned = false;

  private readonly mapReadySub: Subscription;

  /*
   * Subscribe in the constructor — before LeafletDirective's ngOnInit emits mapReady — so a
   * lifecycle-ordering race can't make us miss the event and never focus the map.
   */
  public constructor(
    @Inject(LeafletDirective) private readonly leafletDirective: LeafletDirective,
    @Inject(MapOrganizationAreaService) private readonly mapOrganizationAreaService: MapOrganizationAreaService,
  ) {
    this.mapReadySub = this.leafletDirective.mapReady.subscribe((map: Map) => {
      this.map = map;

      /*
       * dragstart only fires from user interaction (not programmatic fitBounds), so it is a safe
       * signal that we should stop auto-focusing.
       */
      map.on('dragstart', () => {
        this.userPanned = true;
      });
      this.focus();
    });
  }

  public ngOnChanges(): void {
    // A new bounds value (async content, a filter re-run, a selection) re-focuses the map.
    if (this.map) {
      this.focus();
    }
  }

  public ngOnDestroy(): void {
    this.mapReadySub.unsubscribe();
  }

  private focus(): void {
    if (!this.map) {
      return;
    }

    if (this.focusBounds instanceof LatLngBounds && this.focusBounds.isValid()) {
      this.map.fitBounds(this.focusBounds);
      return;
    }

    // Nothing to focus on — fall back to the org area, but don't yank a user who has moved the map.
    if (this.userPanned) {
      return;
    }

    /*
     * Only fall back to the org area if no content bounds arrived while we were fetching (and the
     * user hasn't taken over the map) — otherwise a late org-area response would override content
     * that resolved in the meantime (e.g. the geofence page's async geofence search).
     */
    this.mapOrganizationAreaService.getOrgArea().subscribe(orgBounds => {
      const hasContent = this.focusBounds instanceof LatLngBounds && this.focusBounds.isValid();
      if (orgBounds && !this.userPanned && !hasContent) {
        this.map?.fitBounds(orgBounds);
      }
    });
  }

}
